import { STATUS_LABEL, WorkflowStatus } from "../monitor.types";
import { workflowStatusOverride } from "~/common/workflows/statusLabels";
import { STATUS_PILL_BASE, STATUS_PILL_TONE } from "~/common/workflows/statusPill";
import { warningMarker } from "~/common/workflows/warningAcknowledgement";

const STATUS_CLASSES: Record<WorkflowStatus, string> = {
  failed:
    "bg-primary-error-50 text-primary-error-700 border border-primary-error-200",
  // The shared Needs You pill (statusPill.ts) — this badge is its origin.
  paused_hitl: STATUS_PILL_TONE.needsYou,
  warm_hitl: STATUS_PILL_TONE.needsYou,
  rpa_review: STATUS_PILL_TONE.needsYou,
  // In-flight. Deliberately the neutral white pill, NOT the brand ramp —
  // preserves its pre-brand-sweep look (the old primary-blue-* classes never
  // resolved to CSS, so it always rendered neutral).
  running:
    STATUS_PILL_TONE.neutral,
  // Non-terminal: AM needs to send email. Deliberately a neutral white pill,
  // NOT the brand in-flight tone — this preserves its pre-brand-sweep look
  // (the old primary-blue-* classes never resolved to CSS, so it always
  // rendered neutral). No pulse — not yet HITL-urgent.
  pending_email_send:
    STATUS_PILL_TONE.neutral,
  // Non-terminal cold wait for the carrier's eDoc. Neutral white pill like
  // pending_email_send (pre-brand-sweep look, matches the drawer's neutral
  // "Waiting on the carrier" card). No pulse — reject-only, no AM action.
  pending_edocs:
    STATUS_PILL_TONE.neutral,
  completed:
    "bg-primary-success-50 text-primary-success-700 border border-primary-success-200",
  cancelled:
    "bg-primary-gray-light-100 text-primary-gray-light-600 border border-primary-gray-light-200",
  timeout_cancelled:
    "bg-primary-gray-light-100 text-primary-gray-light-600 border border-primary-gray-light-200",
  rejected:
    "bg-primary-gray-light-100 text-primary-gray-light-600 border border-primary-gray-light-200",
};

const HITL_STATUSES: Set<WorkflowStatus> = new Set(["paused_hitl", "warm_hitl"]);

const FALLBACK_CLASSES =
  "bg-primary-gray-light-100 text-primary-gray-light-600 border border-primary-gray-light-200";

export default function WorkflowStatusBadge({
  status,
  workflowType = null,
  hasWarnings = false,
  needsAcknowledgement = null,
}: {
  status: WorkflowStatus;
  /**
   * The run's workflow_id, for per-workflow wording (WORKFLOW_STATUS_OVERRIDES):
   * a quote comparison awaiting review reads "Review Required", not "Needs
   * You". Absent → the generic label.
   */
  workflowType?: string | null;
  /**
   * Renders a "*" after the label: the run carries ``*_warning`` keys in its
   * result — finished (or parked), but something needs the AM's eye. Same
   * predicate as the drawer's warning banners (extractResultWarnings).
   */
  hasWarnings?: boolean;
  /**
   * Warning acknowledgement (2026-10-06): a warning line on this run is still
   * open. `true` → "*", and a completed run turns amber ("Completed*"); `false` →
   * no "*" (every line acknowledged); null/absent → the `hasWarnings` "*" as
   * before (an API that predates acknowledgement). Never changes the label.
   */
  needsAcknowledgement?: boolean | null;
}) {
  const label =
    workflowStatusOverride(workflowType, status)?.status ?? STATUS_LABEL[status] ?? status;
  const marker = warningMarker({ status, label, needsAcknowledgement, hasWarnings });
  const classes = marker.amber
    ? STATUS_PILL_TONE.warning
    : (STATUS_CLASSES[status] ?? FALLBACK_CLASSES);
  return (
    <span
      className={`${STATUS_PILL_BASE} ${classes} ${HITL_STATUSES.has(status) ? "motion-safe:animate-pulse" : ""}`}
    >
      {label}
      {marker.asterisk && (
        <span
          className="ml-0.5 font-bold"
          title={marker.title ?? undefined}
          aria-label={marker.ariaLabel ? undefined : "has warnings"}
          aria-hidden={marker.ariaLabel ? true : undefined}
          data-testid="status-warning-asterisk"
        >
          *
        </span>
      )}
      {marker.ariaLabel && (
        // A bare span's aria-label is not reliably announced: the words are
        // sr-only text only (R41), with no aria-label or title on the pill.
        <span className="sr-only"> {marker.ariaLabel.slice(label.length).trim()}</span>
      )}
    </span>
  );
}
